// src/components/tools/logistics/BnBManagerTool.jsx
import React, { useState, useEffect, useMemo } from 'react';
import {
  Home, Calendar, Users, ClipboardList, AlertCircle, CheckCircle, 
  Settings, Key, Camera, FileText, ChevronRight, Plus, Search, ChevronLeft,
  ArrowLeft, MoreVertical, Edit2, Trash2, Filter, Save, Loader2, Eye,
  DollarSign, TrendingUp, BedDouble, BedSingle, Bath, Wifi, 
  Coffee, Tv, Wind, CheckSquare, XCircle, LogIn, LogOut
} from 'lucide-react';
import { toolsService } from '../../../services/toolsService';

// ============================================================
// CONSTANTS & CONFIG
// ============================================================

const ViewState = {
  DASHBOARD: 'DASHBOARD',
  BOOKINGS: 'BOOKINGS',
  ROOMS: 'ROOMS',
  GUESTS: 'GUESTS',
  ISSUES: 'ISSUES',
  CHECKIN: 'CHECKIN',
  CHECKOUT: 'CHECKOUT',
  BOOKING_FORM: 'BOOKING_FORM',
  ROOM_FORM: 'ROOM_FORM',
  GUEST_FORM: 'GUEST_FORM',
  ISSUE_FORM: 'ISSUE_FORM'
};

const BookingStatus = {
  PENDING: { id: 'PENDING', label: 'Pending', color: 'orange', icon: ClockIcon },
  CONFIRMED: { id: 'CONFIRMED', label: 'Confirmed', color: 'blue', icon: CheckCircle },
  CHECKED_IN: { id: 'CHECKED_IN', label: 'Checked In', color: 'green', icon: LogIn },
  CHECKED_OUT: { id: 'CHECKED_OUT', label: 'Checked Out', color: 'gray', icon: LogOut },
  CANCELLED: { id: 'CANCELLED', label: 'Cancelled', color: 'red', icon: XCircle }
};

const RoomStatus = {
  VACANT: { id: 'VACANT', label: 'Vacant', color: 'green' },
  OCCUPIED: { id: 'OCCUPIED', label: 'Occupied', color: 'blue' },
  DIRTY: { id: 'DIRTY', label: 'Dirty', color: 'orange' },
  MAINTENANCE: { id: 'MAINTENANCE', label: 'Maintenance', color: 'red' }
};

const IssuePriority = {
  LOW: { id: 'LOW', label: 'Low', color: 'blue' },
  MEDIUM: { id: 'MEDIUM', label: 'Medium', color: 'orange' },
  HIGH: { id: 'HIGH', label: 'High', color: 'red' },
  URGENT: { id: 'URGENT', label: 'Urgent', color: 'rose' }
};

const IssueStatus = {
  OPEN: { id: 'OPEN', label: 'Open', color: 'red' },
  IN_PROGRESS: { id: 'IN_PROGRESS', label: 'In Progress', color: 'blue' },
  RESOLVED: { id: 'RESOLVED', label: 'Resolved', color: 'green' }
};

// Helper function for missing icon
function ClockIcon(props) {
  return (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      width="24" height="24" viewBox="0 0 24 24" 
      fill="none" stroke="currentColor" strokeWidth="2" 
      strokeLinecap="round" strokeLinejoin="round" 
      {...props}
    >
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

const Amenities = [
  { id: 'wifi', label: 'WiFi', icon: Wifi },
  { id: 'tv', label: 'TV', icon: Tv },
  { id: 'ac', label: 'AC', icon: Wind },
  { id: 'coffee', label: 'Coffee', icon: Coffee },
  { id: 'bath', label: 'Bath', icon: Bath },
];

// ============================================================
// HELPER COMPONENTS
// ============================================================

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
    maximumFractionDigits: 0
  }).format(amount || 0);
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString('en-ZA', {
    month: 'short', day: 'numeric', year: 'numeric'
  });
};

const getDateRange = (date, type) => {
  const d = new Date(date);
  if (type === 'week') {
    const day = d.getDay();
    const start = new Date(d);
    start.setDate(d.getDate() - day);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    return { start, end };
  }
  if (type === 'month') {
    const start = new Date(d.getFullYear(), d.getMonth(), 1);
    const end = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    return { start, end };
  }
  return { start: d, end: d };
};

const isDateInRange = (dateStr, start, end) => {
  const date = new Date(dateStr);
  return date >= start && date <= end;
};

const StatCard = ({ icon: Icon, label, value, subValue, color = 'indigo', onClick }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-4 p-5 bg-white rounded-2xl border border-gray-100 hover:border-gray-200 hover:shadow-lg transition-all text-left w-full ${onClick ? 'cursor-pointer' : ''}`}
  >
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center bg-${color}-100`}>
      <Icon className={`w-6 h-6 text-${color}-600`} />
    </div>
    <div className="flex-1">
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      <p className="text-sm text-gray-500">{label}</p>
      {subValue && <p className="text-xs text-gray-400 mt-0.5">{subValue}</p>}
    </div>
    {onClick && <ChevronRight className="w-5 h-5 text-gray-300" />}
  </button>
);

const EmptyState = ({ icon: Icon, title, description, action }) => (
  <div className="flex flex-col items-center justify-center py-16 px-4 text-center bg-white rounded-2xl border border-gray-100">
    <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mb-4">
      <Icon className="w-10 h-10 text-gray-400" />
    </div>
    <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
    <p className="text-gray-500 mb-6 max-w-sm">{description}</p>
    {action}
  </div>
);

// ============================================================
// FORMS
// ============================================================

const BookingForm = ({ booking, rooms, guests, onSave, onCancel, saving }) => {
  const [formData, setFormData] = useState({
    guestName: '', guestEmail: '', guestPhone: '', guestIdNumber: '',
    roomId: '', checkInDate: '', checkOutDate: '',
    guestsCount: 1, status: 'PENDING', notes: '', totalAmount: 0,
    paidAmount: 0, balanceAmount: 0,
    ...booking
  });

  // Calculate nights, total, and balance
  useEffect(() => {
    if (formData.checkInDate && formData.checkOutDate && formData.roomId) {
      const start = new Date(formData.checkInDate);
      const end = new Date(formData.checkOutDate);
      const diffTime = end - start;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)); 
      
      const room = rooms.find(r => r.id === formData.roomId);
      if (room && diffDays > 0) {
        const total = diffDays * (room.baseRate || 0);
        const balance = total - (formData.paidAmount || 0);
        setFormData(prev => ({ ...prev, totalAmount: total, balanceAmount: balance }));
      }
    }
  }, [formData.checkInDate, formData.checkOutDate, formData.roomId, rooms]);

  // Update balance when paid amount changes
  useEffect(() => {
    const balance = (formData.totalAmount || 0) - (formData.paidAmount || 0);
    setFormData(prev => ({ ...prev, balanceAmount: balance }));
  }, [formData.paidAmount, formData.totalAmount]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  // Get min checkout date (day after checkin)
  const getMinCheckoutDate = () => {
    if (!formData.checkInDate) return '';
    const checkin = new Date(formData.checkInDate);
    checkin.setDate(checkin.getDate() + 1);
    return checkin.toISOString().split('T')[0];
  };

  return (
    <form onSubmit={handleSubmit} className="max-w-2xl mx-auto p-6 space-y-6">
      <div className="flex items-center gap-3">
        <button type="button" onClick={onCancel} className="p-2 hover:bg-gray-100 rounded-xl"><ArrowLeft className="w-5 h-5 text-gray-600" /></button>
        <h2 className="text-2xl font-bold text-gray-900">{booking?.id ? 'Edit Booking' : 'New Booking'}</h2>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-4">
        {/* Guest Info Section */}
        <div className="border-b border-gray-100 pb-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <Users className="w-4 h-4" /> Guest Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Guest Name *</label>
              <input 
                required 
                type="text" 
                value={formData.guestName} 
                onChange={e => setFormData({...formData, guestName: e.target.value})} 
                placeholder="Full name"
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent" 
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
              <input 
                type="tel" 
                value={formData.guestPhone} 
                onChange={e => setFormData({...formData, guestPhone: e.target.value})} 
                placeholder="+27..."
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent" 
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input 
                type="email" 
                value={formData.guestEmail} 
                onChange={e => setFormData({...formData, guestEmail: e.target.value})} 
                placeholder="email@example.com"
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent" 
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">ID / Passport</label>
              <input 
                type="text" 
                value={formData.guestIdNumber} 
                onChange={e => setFormData({...formData, guestIdNumber: e.target.value})} 
                placeholder="ID or Passport number"
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent" 
              />
            </div>
          </div>
        </div>

        {/* Room & Dates */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Room *</label>
            <select required value={formData.roomId} onChange={e => setFormData({...formData, roomId: e.target.value})} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent">
              <option value="">Select Room...</option>
              {rooms.filter(r => r.status === 'VACANT' || r.id === booking?.roomId).map(r => (
                <option key={r.id} value={r.id}>{r.name} - {r.type} - {formatCurrency(r.baseRate)}/night</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Check-In *</label>
            <input 
              type="date" 
              required 
              min={new Date().toISOString().split('T')[0]}
              value={formData.checkInDate} 
              onChange={e => {
                const newCheckin = e.target.value;
                setFormData(prev => ({
                  ...prev, 
                  checkInDate: newCheckin,
                  checkOutDate: prev.checkOutDate && prev.checkOutDate <= newCheckin ? '' : prev.checkOutDate
                }));
              }} 
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent" 
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Check-Out *</label>
            <input 
              type="date" 
              required 
              min={getMinCheckoutDate()}
              disabled={!formData.checkInDate}
              value={formData.checkOutDate} 
              onChange={e => setFormData({...formData, checkOutDate: e.target.value})} 
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed" 
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
           <div>
             <label className="block text-sm font-medium text-gray-700 mb-1">Number of Guests</label>
             <input type="number" min="1" value={formData.guestsCount} onChange={e => setFormData({...formData, guestsCount: parseInt(e.target.value) || 1})} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl" />
           </div>
           <div>
             <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
             <select value={formData.status} onChange={e => setFormData({...formData, status: e.target.value})} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl">
               {Object.values(BookingStatus).map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
             </select>
           </div>
        </div>

        {/* Payment Section */}
        <div className="p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl space-y-3">
          <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
            <DollarSign className="w-4 h-4" /> Payment Summary
          </h3>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center p-3 bg-white rounded-lg">
              <p className="text-xs text-gray-500 mb-1">Total</p>
              <p className="text-lg font-bold text-gray-900">{formatCurrency(formData.totalAmount)}</p>
            </div>
            <div className="text-center p-3 bg-white rounded-lg">
              <p className="text-xs text-gray-500 mb-1">Paid</p>
              <input 
                type="number" 
                min="0"
                max={formData.totalAmount}
                value={formData.paidAmount} 
                onChange={e => setFormData({...formData, paidAmount: parseFloat(e.target.value) || 0})} 
                className="w-full text-center text-lg font-bold text-green-600 border-0 bg-transparent focus:ring-0" 
              />
            </div>
            <div className="text-center p-3 bg-white rounded-lg">
              <p className="text-xs text-gray-500 mb-1">Balance</p>
              <p className={`text-lg font-bold ${formData.balanceAmount > 0 ? 'text-red-600' : 'text-green-600'}`}>
                {formatCurrency(formData.balanceAmount)}
              </p>
            </div>
          </div>
        </div>

        <div>
           <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
           <textarea rows={3} value={formData.notes} onChange={e => setFormData({...formData, notes: e.target.value})} placeholder="Special requests, preferences..." className="w-full px-4 py-2.5 border border-gray-200 rounded-xl" />
        </div>

        <div className="flex justify-end pt-4">
          <button type="submit" disabled={saving} className="px-6 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 flex items-center gap-2 transition-colors">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Booking
          </button>
        </div>
      </div>
    </form>
  );
};

const RoomForm = ({ room, onSave, onCancel, saving }) => {
  const [formData, setFormData] = useState({
    name: '', type: 'Double', baseRate: 0, status: 'VACANT',
    amenities: [], notes: '',
    ...room
  });

  const toggleAmenity = (id) => {
    setFormData(prev => ({
      ...prev,
      amenities: prev.amenities.includes(id) 
        ? prev.amenities.filter(a => a !== id)
        : [...prev.amenities, id]
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="max-w-2xl mx-auto p-6 space-y-6">
      <div className="flex items-center gap-3">
        <button type="button" onClick={onCancel} className="p-2 hover:bg-gray-100 rounded-xl"><ArrowLeft className="w-5 h-5 text-gray-600" /></button>
        <h2 className="text-2xl font-bold text-gray-900">{room?.id ? 'Edit Room' : 'New Room'}</h2>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Room Name/Number *</label>
            <input required type="text" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
            <select value={formData.type} onChange={e => setFormData({...formData, type: e.target.value})} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl">
               <option value="Single">Single</option>
               <option value="Double">Double</option>
               <option value="Twin">Twin</option>
               <option value="Suite">Suite</option>
               <option value="Cottage">Cottage</option>
               <option value="Dorm">Dorm</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Base Rate (Per Night)</label>
            <input type="number" required value={formData.baseRate} onChange={e => setFormData({...formData, baseRate: parseFloat(e.target.value)})} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Current Status</label>
            <select value={formData.status} onChange={e => setFormData({...formData, status: e.target.value})} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl">
               {Object.values(RoomStatus).map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </div>
        </div>

        <div>
           <label className="block text-sm font-medium text-gray-700 mb-2">Amenities</label>
           <div className="flex flex-wrap gap-2">
              {Amenities.map(amenity => {
                 const Icon = amenity.icon;
                 const isSelected = formData.amenities.includes(amenity.id);
                 return (
                    <button
                       key={amenity.id} type="button" onClick={() => toggleAmenity(amenity.id)}
                       className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm ${isSelected ? 'bg-indigo-50 border-indigo-200 text-indigo-700' : 'bg-white border-gray-200 text-gray-600'}`}
                    >
                       <Icon className="w-4 h-4" /> {amenity.label}
                    </button>
                 );
              })}
           </div>
        </div>

        <div className="flex justify-end pt-4">
          <button type="submit" disabled={saving} className="px-6 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 flex items-center gap-2">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Room
          </button>
        </div>
      </div>
    </form>
  );
};

const GuestForm = ({ guest, onSave, onCancel, saving }) => {
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', idNumber: '', notes: '',
    ...guest
  });

  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave(formData); }} className="max-w-2xl mx-auto p-6 space-y-6">
       <div className="flex items-center gap-3">
        <button type="button" onClick={onCancel} className="p-2 hover:bg-gray-100 rounded-xl"><ArrowLeft className="w-5 h-5 text-gray-600" /></button>
        <h2 className="text-2xl font-bold text-gray-900">{guest?.id ? 'Edit Guest' : 'New Guest'}</h2>
      </div>
      <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-4">
         <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
            <input required type="text" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl" />
         </div>
         <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
               <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
               <input type="email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl" />
            </div>
            <div>
               <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
               <input type="tel" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl" />
            </div>
         </div>
         <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">ID / Passport Number</label>
            <input type="text" value={formData.idNumber} onChange={e => setFormData({...formData, idNumber: e.target.value})} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl" />
         </div>
         <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes (Preferences, etc.)</label>
            <textarea rows={3} value={formData.notes} onChange={e => setFormData({...formData, notes: e.target.value})} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl" />
         </div>
         <div className="flex justify-end pt-4">
          <button type="submit" disabled={saving} className="px-6 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 flex items-center gap-2">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Guest
          </button>
        </div>
      </div>
    </form>
  );
};

const IssueForm = ({ issue, rooms, onSave, onCancel, saving }) => {
   const [formData, setFormData] = useState({
      title: '', description: '', roomId: '', priority: 'MEDIUM',
      status: 'OPEN', cost: 0,
      ...issue
   });

   return (
      <form onSubmit={(e) => { e.preventDefault(); onSave(formData); }} className="max-w-2xl mx-auto p-6 space-y-6">
         <div className="flex items-center gap-3">
            <button type="button" onClick={onCancel} className="p-2 hover:bg-gray-100 rounded-xl"><ArrowLeft className="w-5 h-5 text-gray-600" /></button>
            <h2 className="text-2xl font-bold text-gray-900">{issue?.id ? 'Edit Issue' : 'Report Issue'}</h2>
         </div>
         <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-4">
            <div>
               <label className="block text-sm font-medium text-gray-700 mb-1">Issue Title *</label>
               <input required type="text" value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} placeholder="e.g. Leaking Tap" className="w-full px-4 py-2.5 border border-gray-200 rounded-xl" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
               <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Room</label>
                  <select required value={formData.roomId} onChange={e => setFormData({...formData, roomId: e.target.value})} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl">
                     <option value="">Select Room...</option>
                     {rooms.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                  </select>
               </div>
               <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                  <select value={formData.priority} onChange={e => setFormData({...formData, priority: e.target.value})} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl">
                     {Object.values(IssuePriority).map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
                  </select>
               </div>
            </div>
            <div>
               <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
               <textarea rows={3} value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
               <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select value={formData.status} onChange={e => setFormData({...formData, status: e.target.value})} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl">
                     {Object.values(IssueStatus).map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
                  </select>
               </div>
               <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cost to Repair</label>
                  <input type="number" value={formData.cost} onChange={e => setFormData({...formData, cost: parseFloat(e.target.value)})} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl" />
               </div>
            </div>
            <div className="flex justify-end pt-4">
               <button type="submit" disabled={saving} className="px-6 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 flex items-center gap-2">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Issue
               </button>
            </div>
         </div>
      </form>
   );
};

const BookingCalendar = ({ bookings, rooms, onBookingClick, onNewBooking }) => {
  const [viewMode, setViewMode] = useState('week'); // 'week' or 'month'
  const [currentDate, setCurrentDate] = useState(new Date());

  const navigate = (direction) => {
    const newDate = new Date(currentDate);
    if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() + (direction * 7));
    } else {
      newDate.setMonth(newDate.getMonth() + direction);
    }
    setCurrentDate(newDate);
  };

  const getDaysArray = () => {
    const days = [];
    if (viewMode === 'week') {
      const startOfWeek = new Date(currentDate);
      startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());
      for (let i = 0; i < 7; i++) {
        const day = new Date(startOfWeek);
        day.setDate(startOfWeek.getDate() + i);
        days.push(day);
      }
    } else {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      const startDay = new Date(firstDay);
      startDay.setDate(firstDay.getDate() - firstDay.getDay());
      
      for (let i = 0; i < 42; i++) {
        const day = new Date(startDay);
        day.setDate(startDay.getDate() + i);
        if (day <= lastDay || days.length < 35) {
          days.push(day);
        }
      }
    }
    return days;
  };

  const getBookingsForDay = (day, roomId) => {
    const dayStr = day.toISOString().split('T')[0];
    return bookings.filter(b => {
      if (b.roomId !== roomId) return false;
      if (b.status === 'CANCELLED') return false;
      return dayStr >= b.checkInDate && dayStr < b.checkOutDate;
    });
  };

  const getBookingStyle = (booking) => {
    const statusColors = {
      PENDING: 'bg-orange-100 border-orange-300 text-orange-800',
      CONFIRMED: 'bg-blue-100 border-blue-300 text-blue-800',
      CHECKED_IN: 'bg-green-100 border-green-300 text-green-800',
      CHECKED_OUT: 'bg-gray-100 border-gray-300 text-gray-600'
    };
    return statusColors[booking.status] || 'bg-gray-100 border-gray-300';
  };

  const days = getDaysArray();
  const today = new Date().toISOString().split('T')[0];

  const maintenanceRooms = rooms.filter(r => r.status === 'MAINTENANCE');

  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 rounded-lg">
            <ChevronLeft className="w-5 h-5 text-gray-600" />
          </button>
          <h3 className="font-semibold text-gray-900 min-w-[200px] text-center">
            {viewMode === 'week' 
              ? `${days[0]?.toLocaleDateString('en-ZA', { month: 'short', day: 'numeric' })} - ${days[6]?.toLocaleDateString('en-ZA', { month: 'short', day: 'numeric', year: 'numeric' })}`
              : currentDate.toLocaleDateString('en-ZA', { month: 'long', year: 'numeric' })
            }
          </h3>
          <button onClick={() => navigate(1)} className="p-2 hover:bg-gray-100 rounded-lg">
            <ChevronRight className="w-5 h-5 text-gray-600" />
          </button>
        </div>
        <div className="flex items-center gap-2">
          <button 
            onClick={() => setCurrentDate(new Date())} 
            className="px-3 py-1.5 text-sm text-indigo-600 hover:bg-indigo-50 rounded-lg"
          >
            Today
          </button>
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button 
              onClick={() => setViewMode('week')}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${viewMode === 'week' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'}`}
            >
              Week
            </button>
            <button 
              onClick={() => setViewMode('month')}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${viewMode === 'month' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'}`}
            >
              Month
            </button>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 px-4 py-2 bg-gray-50 border-b border-gray-100 text-xs">
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-orange-200 border border-orange-300" /> Pending</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-blue-200 border border-blue-300" /> Confirmed</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-green-200 border border-green-300" /> Checked In</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-200 border border-red-300" /> Maintenance</span>
      </div>

      {/* Calendar Grid */}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[800px]">
          <thead>
            <tr className="bg-gray-50">
              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 w-28 sticky left-0 bg-gray-50">Room</th>
              {days.slice(0, viewMode === 'week' ? 7 : 7).map((day, i) => (
                <th key={i} className="px-1 py-2 text-center text-xs font-semibold text-gray-600">
                  {day.toLocaleDateString('en-ZA', { weekday: 'short' })}
                </th>
              ))}
            </tr>
            {viewMode === 'week' && (
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="sticky left-0 bg-gray-50"></th>
                {days.map((day, i) => {
                  const isToday = day.toISOString().split('T')[0] === today;
                  return (
                    <th key={i} className={`px-1 py-1 text-center text-sm ${isToday ? 'text-indigo-600 font-bold' : 'text-gray-700'}`}>
                      {day.getDate()}
                    </th>
                  );
                })}
              </tr>
            )}
          </thead>
          <tbody>
            {viewMode === 'week' ? (
              // Week view - rooms as rows
              rooms.map(room => {
                const isMaintenance = room.status === 'MAINTENANCE';
                return (
                  <tr key={room.id} className="border-b border-gray-100">
                    <td className={`px-3 py-2 text-sm font-medium sticky left-0 bg-white ${isMaintenance ? 'text-red-600' : 'text-gray-900'}`}>
                      {room.name}
                      {isMaintenance && <span className="ml-1 text-[10px] bg-red-100 text-red-600 px-1 rounded">🔧</span>}
                    </td>
                    {days.map((day, i) => {
                      const dayStr = day.toISOString().split('T')[0];
                      const isToday = dayStr === today;
                      const dayBookings = getBookingsForDay(day, room.id);
                      
                      return (
                        <td 
                          key={i} 
                          className={`px-1 py-1 h-12 border-l border-gray-100 ${isToday ? 'bg-indigo-50/50' : ''} ${isMaintenance ? 'bg-red-50' : ''}`}
                          onClick={() => !isMaintenance && dayBookings.length === 0 && onNewBooking?.(room.id, dayStr)}
                        >
                          {dayBookings.map(booking => (
                            <button
                              key={booking.id}
                              onClick={(e) => { e.stopPropagation(); onBookingClick?.(booking); }}
                              className={`w-full px-1 py-0.5 text-[10px] rounded border truncate ${getBookingStyle(booking)} hover:opacity-80`}
                            >
                              {booking.guestName || 'Guest'}
                            </button>
                          ))}
                          {isMaintenance && dayBookings.length === 0 && (
                            <div className="w-full h-full bg-red-100/50 rounded flex items-center justify-center">
                              <span className="text-[10px] text-red-400">N/A</span>
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            ) : (
              // Month view - calendar grid
              Array.from({ length: Math.ceil(days.length / 7) }).map((_, weekIdx) => (
                <tr key={weekIdx} className="border-b border-gray-100">
                  <td className="px-3 py-2 text-xs text-gray-500 sticky left-0 bg-white">
                    Wk {weekIdx + 1}
                  </td>
                  {days.slice(weekIdx * 7, (weekIdx + 1) * 7).map((day, i) => {
                    const dayStr = day.toISOString().split('T')[0];
                    const isToday = dayStr === today;
                    const isCurrentMonth = day.getMonth() === currentDate.getMonth();
                    const dayBookings = bookings.filter(b => 
                      b.status !== 'CANCELLED' && 
                      dayStr >= b.checkInDate && dayStr < b.checkOutDate
                    );
                    const occupiedRoomIds = [...new Set(dayBookings.map(b => b.roomId))];
                    
                    return (
                      <td 
                        key={i} 
                        className={`px-1 py-1 h-20 border-l border-gray-100 align-top ${isToday ? 'bg-indigo-50/50' : ''} ${!isCurrentMonth ? 'bg-gray-50/50' : ''}`}
                      >
                        <div className={`text-xs mb-1 ${isToday ? 'text-indigo-600 font-bold' : isCurrentMonth ? 'text-gray-700' : 'text-gray-400'}`}>
                          {day.getDate()}
                        </div>
                        <div className="space-y-0.5">
                          {dayBookings.slice(0, 2).map(booking => (
                            <button
                              key={booking.id}
                              onClick={() => onBookingClick?.(booking)}
                              className={`w-full px-1 py-0.5 text-[9px] rounded border truncate ${getBookingStyle(booking)} hover:opacity-80`}
                            >
                              {rooms.find(r => r.id === booking.roomId)?.name}
                            </button>
                          ))}
                          {dayBookings.length > 2 && (
                            <div className="text-[9px] text-gray-500 text-center">+{dayBookings.length - 2} more</div>
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Maintenance Rooms Summary */}
      {maintenanceRooms.length > 0 && (
        <div className="px-4 py-3 bg-red-50 border-t border-red-100">
          <p className="text-xs text-red-700 font-medium">
            🔧 Rooms under maintenance: {maintenanceRooms.map(r => r.name).join(', ')}
          </p>
        </div>
      )}
    </div>
  );
};

const IncomeStats = ({ bookings }) => {
  const [period, setPeriod] = useState('week');
  
  const stats = useMemo(() => {
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];
    
    // Today's income
    const todayIncome = bookings
      .filter(b => b.checkInDate === todayStr && b.status !== 'CANCELLED')
      .reduce((sum, b) => sum + (b.paidAmount || 0), 0);
    
    // This week's income
    const { start: weekStart, end: weekEnd } = getDateRange(today, 'week');
    const weekIncome = bookings
      .filter(b => {
        const checkIn = new Date(b.checkInDate);
        return checkIn >= weekStart && checkIn <= weekEnd && b.status !== 'CANCELLED';
      })
      .reduce((sum, b) => sum + (b.paidAmount || 0), 0);
    
    // This month's income
    const { start: monthStart, end: monthEnd } = getDateRange(today, 'month');
    const monthIncome = bookings
      .filter(b => {
        const checkIn = new Date(b.checkInDate);
        return checkIn >= monthStart && checkIn <= monthEnd && b.status !== 'CANCELLED';
      })
      .reduce((sum, b) => sum + (b.paidAmount || 0), 0);

    // Outstanding balance
    const totalOutstanding = bookings
      .filter(b => b.status !== 'CANCELLED' && b.status !== 'CHECKED_OUT')
      .reduce((sum, b) => sum + ((b.totalAmount || 0) - (b.paidAmount || 0)), 0);

    return { todayIncome, weekIncome, monthIncome, totalOutstanding };
  }, [bookings]);

  return (
    <div className="bg-gradient-to-br from-indigo-600 to-purple-700 rounded-2xl p-6 text-white">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold flex items-center gap-2">
          <DollarSign className="w-5 h-5" /> Revenue Overview
        </h3>
        <div className="flex bg-white/20 rounded-lg p-0.5">
          {['day', 'week', 'month'].map(p => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-3 py-1 text-xs rounded-md capitalize transition-colors ${period === p ? 'bg-white text-indigo-700' : 'text-white/80 hover:text-white'}`}
            >
              {p}
            </button>
          ))}
        </div>
      </div>
      
      <div className="text-center mb-4">
        <p className="text-white/70 text-sm mb-1">
          {period === 'day' ? "Today's" : period === 'week' ? "This Week's" : "This Month's"} Revenue
        </p>
        <p className="text-4xl font-bold">
          {formatCurrency(period === 'day' ? stats.todayIncome : period === 'week' ? stats.weekIncome : stats.monthIncome)}
        </p>
      </div>

      <div className="grid grid-cols-3 gap-3 pt-4 border-t border-white/20">
        <div className="text-center">
          <p className="text-white/70 text-xs">Today</p>
          <p className="font-semibold">{formatCurrency(stats.todayIncome)}</p>
        </div>
        <div className="text-center border-x border-white/20">
          <p className="text-white/70 text-xs">This Week</p>
          <p className="font-semibold">{formatCurrency(stats.weekIncome)}</p>
        </div>
        <div className="text-center">
          <p className="text-white/70 text-xs">This Month</p>
          <p className="font-semibold">{formatCurrency(stats.monthIncome)}</p>
        </div>
      </div>

      {stats.totalOutstanding > 0 && (
        <div className="mt-4 pt-3 border-t border-white/20">
          <div className="flex justify-between items-center text-sm">
            <span className="text-white/70">Outstanding Balance</span>
            <span className="font-semibold text-orange-300">{formatCurrency(stats.totalOutstanding)}</span>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================
// UPCOMING ARRIVALS COMPONENT
// ============================================================

const UpcomingArrivals = ({ bookings, rooms, guests, onBookingClick, onCheckIn }) => {
  const upcoming = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const nextWeek = new Date(today);
    nextWeek.setDate(today.getDate() + 7);
    
    return bookings
      .filter(b => {
        const checkIn = new Date(b.checkInDate);
        return checkIn >= today && checkIn <= nextWeek && ['PENDING', 'CONFIRMED'].includes(b.status);
      })
      .sort((a, b) => new Date(a.checkInDate) - new Date(b.checkInDate))
      .slice(0, 5);
  }, [bookings]);

  const todayStr = new Date().toISOString().split('T')[0];

  if (upcoming.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 p-6">
        <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Calendar className="w-5 h-5 text-indigo-600" /> Upcoming Arrivals
        </h3>
        <div className="text-center py-8 text-gray-500">
          <LogIn className="w-10 h-10 mx-auto mb-2 text-gray-300" />
          <p>No upcoming arrivals this week</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-6">
      <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
        <Calendar className="w-5 h-5 text-indigo-600" /> Upcoming Arrivals
        <span className="ml-auto text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full">{upcoming.length}</span>
      </h3>
      <div className="space-y-3">
        {upcoming.map(booking => {
          const room = rooms.find(r => r.id === booking.roomId);
          const isToday = booking.checkInDate === todayStr;
          
          return (
            <div 
              key={booking.id}
              className={`p-3 rounded-xl border ${isToday ? 'border-green-200 bg-green-50' : 'border-gray-100 bg-gray-50'} hover:shadow-sm transition-shadow`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-gray-900">{booking.guestName || 'Guest'}</p>
                    {isToday && (
                      <span className="text-[10px] bg-green-500 text-white px-1.5 py-0.5 rounded-full font-medium">TODAY</span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mt-0.5">
                    {room?.name} • {booking.guestsCount || 1} guest{(booking.guestsCount || 1) > 1 ? 's' : ''}
                  </p>
                  <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                    <span>{new Date(booking.checkInDate).toLocaleDateString('en-ZA', { weekday: 'short', month: 'short', day: 'numeric' })}</span>
                    <span>→</span>
                    <span>{new Date(booking.checkOutDate).toLocaleDateString('en-ZA', { weekday: 'short', month: 'short', day: 'numeric' })}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {isToday && booking.status === 'CONFIRMED' && (
                    <button 
                      onClick={() => onCheckIn?.(booking)}
                      className="px-3 py-1.5 bg-green-600 text-white text-xs rounded-lg hover:bg-green-700 flex items-center gap-1"
                    >
                      <LogIn className="w-3 h-3" /> Check In
                    </button>
                  )}
                  <button 
                    onClick={() => onBookingClick?.(booking)}
                    className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                </div>
              </div>
              {booking.balanceAmount > 0 && (
                <div className="mt-2 pt-2 border-t border-gray-200 flex justify-between text-xs">
                  <span className="text-gray-500">Balance due:</span>
                  <span className="text-red-600 font-medium">{formatCurrency(booking.balanceAmount)}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ============================================================
// MAIN COMPONENT
// ============================================================

const BnBManagerTool = ({ bookId, onBack, customStyling }) => {
  const [view, setView] = useState(ViewState.DASHBOARD);
  
  // Data
  const [bookings, setBookings] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [guests, setGuests] = useState([]);
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Selection
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [selectedGuest, setSelectedGuest] = useState(null);
  const [selectedIssue, setSelectedIssue] = useState(null);

  useEffect(() => {
    loadData();
  }, [bookId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [b, r, g, i] = await Promise.all([
        toolsService.getToolData(bookId, 'bnb-bookings'),
        toolsService.getToolData(bookId, 'bnb-rooms'),
        toolsService.getToolData(bookId, 'bnb-guests'),
        toolsService.getToolData(bookId, 'bnb-issues')
      ]);
      setBookings(b);
      setRooms(r);
      setGuests(g);
      setIssues(i);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const stats = useMemo(() => {
    const today = new Date().toISOString().split('T')[0];
    const checkIns = bookings.filter(b => b.checkInDate === today && b.status === 'CONFIRMED').length;
    const checkOuts = bookings.filter(b => b.checkOutDate === today && b.status === 'CHECKED_IN').length;
    const occupiedRooms = rooms.filter(r => r.status === 'OCCUPIED').length;
    const occupancyRate = rooms.length ? Math.round((occupiedRooms / rooms.length) * 100) : 0;
    const openIssues = issues.filter(i => i.status !== 'RESOLVED').length;

    return { checkIns, checkOuts, occupancyRate, openIssues };
  }, [bookings, rooms, issues]);

  // CRUD Handlers
  const handleSave = async (collection, data, returnView, resetSelected) => {
   setSaving(true);
   try {
      // If saving a booking, handle guest creation/update
      if (collection === 'bnb-bookings' && data.guestName) {
         // Check if guest exists by name or create new
         let existingGuest = guests.find(g => 
         g.name?.toLowerCase() === data.guestName?.toLowerCase() ||
         (data.guestIdNumber && g.idNumber === data.guestIdNumber)
         );
         
         const guestData = {
         name: data.guestName,
         email: data.guestEmail || '',
         phone: data.guestPhone || '',
         idNumber: data.guestIdNumber || '',
         lastBooking: new Date().toISOString(),
         updatedAt: new Date().toISOString()
         };

         if (existingGuest) {
         // Update existing guest
         await toolsService.saveToolData(bookId, 'bnb-guests', { ...existingGuest, ...guestData });
         data.guestId = existingGuest.id;
         } else {
         // Create new guest
         const newGuestId = await toolsService.saveToolData(bookId, 'bnb-guests', {
            ...guestData,
            createdAt: new Date().toISOString()
         });
         data.guestId = newGuestId;
         }
      }

      await toolsService.saveToolData(bookId, collection, data);
      await loadData();
      resetSelected(null);
      setView(returnView);
   } catch (error) {
      console.error('Save error:', error);
   } finally {
      setSaving(false);
   }
   };

  const handleDelete = async (collection, id) => {
    if (!window.confirm('Delete this item?')) return;
    try {
      await toolsService.deleteToolData(bookId, collection, id);
      await loadData();
    } catch (err) { console.error(err); }
  };

  const handleRoomStatusChange = async (roomId, newStatus) => {
     const room = rooms.find(r => r.id === roomId);
     if(room) {
        await toolsService.saveToolData(bookId, 'bnb-rooms', { ...room, status: newStatus });
        loadData();
     }
  };

  // Renderers
  const renderDashboard = () => {
   const handleCheckIn = async (booking) => {
      if (window.confirm(`Check in ${booking.guestName || 'guest'} to ${rooms.find(r => r.id === booking.roomId)?.name}?`)) {
         await handleSave('bnb-bookings', { ...booking, status: 'CHECKED_IN' }, ViewState.DASHBOARD, () => {});
         // Update room status to occupied
         const room = rooms.find(r => r.id === booking.roomId);
         if (room) {
         await toolsService.saveToolData(bookId, 'bnb-rooms', { ...room, status: 'OCCUPIED' });
         loadData();
         }
      }
   };

   return (
      <div className="space-y-6">
         {/* Top Stats Row */}
         <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
         <StatCard icon={LogIn} label="Today's Check-ins" value={stats.checkIns} color="blue" onClick={() => setView(ViewState.BOOKINGS)} />
         <StatCard icon={LogOut} label="Today's Check-outs" value={stats.checkOuts} color="orange" onClick={() => setView(ViewState.BOOKINGS)} />
         <StatCard icon={Home} label="Occupancy Rate" value={`${stats.occupancyRate}%`} subValue={`${rooms.filter(r => r.status === 'OCCUPIED').length}/${rooms.length} rooms`} color="green" onClick={() => setView(ViewState.ROOMS)} />
         <StatCard icon={AlertCircle} label="Open Issues" value={stats.openIssues} color="red" onClick={() => setView(ViewState.ISSUES)} />
         </div>

         {/* Income & Arrivals Row */}
         <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
         <IncomeStats bookings={bookings} />
         <UpcomingArrivals 
            bookings={bookings} 
            rooms={rooms} 
            guests={guests}
            onBookingClick={(b) => { setSelectedBooking(b); setView(ViewState.BOOKING_FORM); }}
            onCheckIn={handleCheckIn}
         />
         </div>

         {/* Calendar */}
         <BookingCalendar 
         bookings={bookings}
         rooms={rooms}
         onBookingClick={(b) => { setSelectedBooking(b); setView(ViewState.BOOKING_FORM); }}
         onNewBooking={(roomId, date) => {
            setSelectedBooking({ roomId, checkInDate: date, checkOutDate: '' });
            setView(ViewState.BOOKING_FORM);
         }}
         />

         {/* Room Status Quick View */}
         <div className="bg-white rounded-2xl border border-gray-100 p-6">
         <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Home className="w-5 h-5 text-indigo-600" /> Room Status
         </h3>
         <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {rooms.map(room => {
               const statusColor = RoomStatus[room.status]?.color || 'gray';
               return (
               <button
                  key={room.id}
                  onClick={() => { setSelectedRoom(room); setView(ViewState.ROOM_FORM); }}
                  className={`p-3 rounded-xl border-2 border-${statusColor}-200 bg-${statusColor}-50 hover:shadow-md transition-all text-left`}
               >
                  <p className="font-medium text-gray-900 text-sm">{room.name}</p>
                  <p className={`text-xs text-${statusColor}-600 mt-0.5`}>{RoomStatus[room.status]?.label}</p>
               </button>
               );
            })}
         </div>
         </div>
      </div>
   );
   };

  const renderBookings = () => (
     <div className="space-y-4">
        <div className="flex justify-between items-center bg-white p-2 rounded-xl border border-gray-100">
           <h2 className="text-lg font-bold text-gray-800 ml-2">Bookings</h2>
           <button onClick={() => { setSelectedBooking(null); setView(ViewState.BOOKING_FORM); }} className="px-4 py-2 bg-indigo-600 text-white rounded-lg flex items-center gap-2 hover:bg-indigo-700"><Plus className="w-4 h-4"/> New Booking</button>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
           {bookings.length === 0 ? <EmptyState icon={Calendar} title="No Bookings" description="Create your first reservation." /> : (
              <table className="w-full text-left">
                 <thead className="bg-gray-50 text-gray-600 text-xs uppercase font-semibold">
                    <tr>
                       <th className="px-6 py-3">Guest</th>
                       <th className="px-6 py-3">Room</th>
                       <th className="px-6 py-3">Dates</th>
                       <th className="px-6 py-3">Status</th>
                       <th className="px-6 py-3">Total</th>
                       <th className="px-6 py-3"></th>
                    </tr>
                 </thead>
                 <tbody className="divide-y divide-gray-100">
                    {bookings.map(b => (
                       <tr key={b.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 font-medium text-gray-900">{b.guestName || guests.find(g => g.id === b.guestId)?.name || 'Unknown'}</td>
                          <td className="px-6 py-4 text-gray-600">{rooms.find(r => r.id === b.roomId)?.name}</td>
                          <td className="px-6 py-4 text-gray-600 text-sm">{formatDate(b.checkInDate)} - {formatDate(b.checkOutDate)}</td>
                          <td className="px-6 py-4"><span className={`text-xs px-2 py-1 rounded-full bg-${BookingStatus[b.status]?.color}-100 text-${BookingStatus[b.status]?.color}-700`}>{BookingStatus[b.status]?.label}</span></td>
                          <td className="px-6 py-4 text-gray-900 font-medium">{formatCurrency(b.totalAmount)}</td>
                          <td className="px-6 py-4 text-right">
                             <button onClick={() => { setSelectedBooking(b); setView(ViewState.BOOKING_FORM); }} className="text-gray-400 hover:text-indigo-600 mr-2"><Edit2 className="w-4 h-4"/></button>
                             <button onClick={() => handleDelete('bnb-bookings', b.id)} className="text-gray-400 hover:text-red-600"><Trash2 className="w-4 h-4"/></button>
                          </td>
                       </tr>
                    ))}
                 </tbody>
              </table>
           )}
        </div>
     </div>
  );

  const renderRooms = () => (
     <div className="space-y-4">
        <div className="flex justify-between items-center bg-white p-2 rounded-xl border border-gray-100">
           <h2 className="text-lg font-bold text-gray-800 ml-2">Rooms</h2>
           <button onClick={() => { setSelectedRoom(null); setView(ViewState.ROOM_FORM); }} className="px-4 py-2 bg-indigo-600 text-white rounded-lg flex items-center gap-2 hover:bg-indigo-700"><Plus className="w-4 h-4"/> Add Room</button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
           {rooms.map(room => (
              <div key={room.id} className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm hover:shadow-md transition-all">
                 <div className={`h-2 w-full bg-${RoomStatus[room.status]?.color}-500`} />
                 <div className="p-4">
                    <div className="flex justify-between items-start mb-2">
                       <h3 className="font-bold text-gray-900 text-lg">{room.name}</h3>
                       <button onClick={() => { setSelectedRoom(room); setView(ViewState.ROOM_FORM); }} className="text-gray-400 hover:text-indigo-600"><Edit2 className="w-4 h-4"/></button>
                    </div>
                    <p className="text-sm text-gray-500 mb-4">{room.type} • {formatCurrency(room.baseRate)}/night</p>
                    <div className="flex flex-wrap gap-2 mb-4">
                       {Object.keys(RoomStatus).map(statusKey => (
                          <button 
                             key={statusKey}
                             onClick={() => handleRoomStatusChange(room.id, statusKey)}
                             className={`w-2 h-2 rounded-full ${room.status === statusKey ? `bg-${RoomStatus[statusKey].color}-500 ring-2 ring-offset-1 ring-${RoomStatus[statusKey].color}-300` : 'bg-gray-200'}`}
                             title={RoomStatus[statusKey].label}
                          />
                       ))}
                       <span className="text-xs text-gray-500 ml-2">{RoomStatus[room.status]?.label}</span>
                    </div>
                    <div className="flex gap-2 text-gray-400">
                       {room.amenities?.includes('wifi') && <Wifi className="w-4 h-4"/>}
                       {room.amenities?.includes('tv') && <Tv className="w-4 h-4"/>}
                       {room.amenities?.includes('coffee') && <Coffee className="w-4 h-4"/>}
                    </div>
                 </div>
              </div>
           ))}
        </div>
     </div>
  );

  const renderGuests = () => (
     <div className="space-y-4">
        <div className="flex justify-between items-center bg-white p-2 rounded-xl border border-gray-100">
           <h2 className="text-lg font-bold text-gray-800 ml-2">Guests</h2>
           <button onClick={() => { setSelectedGuest(null); setView(ViewState.GUEST_FORM); }} className="px-4 py-2 bg-indigo-600 text-white rounded-lg flex items-center gap-2 hover:bg-indigo-700"><Plus className="w-4 h-4"/> Add Guest</button>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
           <table className="w-full text-left">
              <thead className="bg-gray-50 text-gray-600 text-xs uppercase font-semibold">
                 <tr><th className="px-6 py-3">Name</th><th className="px-6 py-3">Contact</th><th className="px-6 py-3">ID Number</th><th className="px-6 py-3"></th></tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                 {guests.map(g => (
                    <tr key={g.id} className="hover:bg-gray-50">
                       <td className="px-6 py-4 font-medium text-gray-900">{g.name}</td>
                       <td className="px-6 py-4 text-gray-600 text-sm">{g.email}<br/>{g.phone}</td>
                       <td className="px-6 py-4 text-gray-600 text-sm">{g.idNumber || '-'}</td>
                       <td className="px-6 py-4 text-right">
                          <button onClick={() => { setSelectedGuest(g); setView(ViewState.GUEST_FORM); }} className="text-gray-400 hover:text-indigo-600 mr-2"><Edit2 className="w-4 h-4"/></button>
                          <button onClick={() => handleDelete('bnb-guests', g.id)} className="text-gray-400 hover:text-red-600"><Trash2 className="w-4 h-4"/></button>
                       </td>
                    </tr>
                 ))}
              </tbody>
           </table>
        </div>
     </div>
  );

  const renderIssues = () => (
     <div className="h-full flex flex-col overflow-hidden">
        <div className="flex justify-between items-center mb-4 shrink-0 bg-white p-2 rounded-xl border border-gray-100">
           <h2 className="text-lg font-bold text-gray-800 ml-2">Maintenance & Issues</h2>
           <button onClick={() => { setSelectedIssue(null); setView(ViewState.ISSUE_FORM); }} className="px-4 py-2 bg-indigo-600 text-white rounded-lg flex items-center gap-2 hover:bg-indigo-700"><Plus className="w-4 h-4"/> Report Issue</button>
        </div>
        <div className="flex-1 overflow-x-auto pb-4">
           <div className="flex gap-4 h-full min-w-[900px]">
              {Object.values(IssueStatus).map(status => (
                 <div key={status.id} className="flex-1 bg-gray-50 rounded-2xl p-4 flex flex-col border border-gray-100">
                    <h3 className={`font-bold text-${status.color}-700 mb-3`}>{status.label}</h3>
                    <div className="space-y-3 overflow-y-auto flex-1">
                       {issues.filter(i => i.status === status.id).map(issue => (
                          <div key={issue.id} onClick={() => { setSelectedIssue(issue); setView(ViewState.ISSUE_FORM); }} className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm hover:shadow-md cursor-pointer">
                             <div className="flex justify-between items-start mb-1">
                                <h4 className="font-medium text-gray-900">{issue.title}</h4>
                                <span className={`text-[10px] px-1.5 py-0.5 rounded bg-${IssuePriority[issue.priority].color}-100 text-${IssuePriority[issue.priority].color}-700 uppercase`}>{IssuePriority[issue.priority].label}</span>
                             </div>
                             <p className="text-xs text-gray-500 mb-2">{rooms.find(r => r.id === issue.roomId)?.name}</p>
                             <p className="text-sm text-gray-600 line-clamp-2">{issue.description}</p>
                          </div>
                       ))}
                    </div>
                 </div>
              ))}
           </div>
        </div>
     </div>
  );

  if (loading) return <div className="flex justify-center h-64 items-center"><Loader2 className="w-10 h-10 animate-spin text-indigo-600" /></div>;

  return (
    <div className="h-full flex flex-col bg-gray-50/50">
      <div className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="p-2 hover:bg-gray-100 rounded-xl"><ArrowLeft className="w-5 h-5 text-gray-600" /></button>
          <h1 className="text-xl font-bold text-gray-900">BnB Manager</h1>
          <div className="h-6 w-px bg-gray-200 mx-2" />
          <nav className="flex gap-1 overflow-x-auto">
             <button onClick={() => setView(ViewState.DASHBOARD)} className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${view === ViewState.DASHBOARD ? 'bg-indigo-50 text-indigo-600' : 'text-gray-600 hover:bg-gray-50'}`}>Dashboard</button>
             <button onClick={() => setView(ViewState.BOOKINGS)} className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${view === ViewState.BOOKINGS ? 'bg-indigo-50 text-indigo-600' : 'text-gray-600 hover:bg-gray-50'}`}>Bookings</button>
             <button onClick={() => setView(ViewState.ROOMS)} className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${view === ViewState.ROOMS ? 'bg-indigo-50 text-indigo-600' : 'text-gray-600 hover:bg-gray-50'}`}>Rooms</button>
             <button onClick={() => setView(ViewState.GUESTS)} className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${view === ViewState.GUESTS ? 'bg-indigo-50 text-indigo-600' : 'text-gray-600 hover:bg-gray-50'}`}>Guests</button>
             <button onClick={() => setView(ViewState.ISSUES)} className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${view === ViewState.ISSUES ? 'bg-indigo-50 text-indigo-600' : 'text-gray-600 hover:bg-gray-50'}`}>Issues</button>
          </nav>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-7xl mx-auto h-full">
          {view === ViewState.DASHBOARD && renderDashboard()}
          {view === ViewState.BOOKINGS && renderBookings()}
          {view === ViewState.ROOMS && renderRooms()}
          {view === ViewState.GUESTS && renderGuests()}
          {view === ViewState.ISSUES && renderIssues()}
          
          {view === ViewState.BOOKING_FORM && <BookingForm booking={selectedBooking} rooms={rooms} guests={guests} onSave={(data) => handleSave('bnb-bookings', data, ViewState.BOOKINGS, setSelectedBooking)} onCancel={() => setView(ViewState.BOOKINGS)} saving={saving} />}
          {view === ViewState.ROOM_FORM && <RoomForm room={selectedRoom} onSave={(data) => handleSave('bnb-rooms', data, ViewState.ROOMS, setSelectedRoom)} onCancel={() => setView(ViewState.ROOMS)} saving={saving} />}
          {view === ViewState.GUEST_FORM && <GuestForm guest={selectedGuest} onSave={(data) => handleSave('bnb-guests', data, ViewState.GUESTS, setSelectedGuest)} onCancel={() => setView(ViewState.GUESTS)} saving={saving} />}
          {view === ViewState.ISSUE_FORM && <IssueForm issue={selectedIssue} rooms={rooms} onSave={(data) => handleSave('bnb-issues', data, ViewState.ISSUES, setSelectedIssue)} onCancel={() => setView(ViewState.ISSUES)} saving={saving} />}
        </div>
      </div>
    </div>
  );
};

export default BnBManagerTool;